import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getAllGuestAccountsForUser,
  enrichGuestAccountsForDisplay,
  getGuestAccountPreview,
  mergeGuestAccountAndJoinClubs,
  validateMergeEligibility,
  reconsiderGuestAccountMerge,
} from '../../services/guestAccountLinkingService';
import GuestAccountDeclineModal from './GuestAccountDeclineModal';
import GuestAccountBulkMergeModal from './GuestAccountBulkMergeModal';
import GuestAccountSummaryCard from './GuestAccountSummaryCard';
import GuestLinkActionChooser from './GuestLinkActionChooser';
import ConfirmationModal from '../common/ConfirmationModal';
import Button from '../common/Button';
import { Card } from '../common/Card';
import { Heading, Text } from '../common/Typography';
import { homeGuestMergeShell, homeSectionHeading } from '../home/homeDashboardStyles';

function resolveSelectedGuestId(guests, preferredId = null) {
  const list = guests || [];
  if (list.length === 0) return null;
  if (list.length === 1) return list[0].id;
  if (preferredId && list.some((g) => g.id === preferredId)) return preferredId;
  return null;
}

/**
 * GuestAccountMergesSection — pending / declined guest merges (Home & Player Hub).
 */
const GuestAccountMergesSection = ({
  currentUser,
  profileHubSection = false,
  homeDashboardSection = false,
  onGuestsResolved,
}) => {
  const navigate = useNavigate();
  const [pendingGuests, setPendingGuests] = useState([]);
  const [declinedGuests, setDeclinedGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGuestId, setSelectedGuestId] = useState(null);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showBulkMergeModal, setShowBulkMergeModal] = useState(false);
  const [showDeclined, setShowDeclined] = useState(false);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [mergePreview, setMergePreview] = useState(null);
  const [merging, setMerging] = useState(false);
  const [mergeSuccess, setMergeSuccess] = useState(null);

  const loadGuestAccounts = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      setLoading(true);
      setError(null);
      setMergeSuccess(null);
      const result = await getAllGuestAccountsForUser(currentUser.id);
      const enrichedPending = await enrichGuestAccountsForDisplay(result.pending || []);
      setPendingGuests(enrichedPending);
      setDeclinedGuests(result.declined || []);
      setSelectedGuestId((prev) =>
        resolveSelectedGuestId(enrichedPending, prev)
      );
    } catch (err) {
      console.error('Error loading guest accounts:', err);
      setError('Failed to load guest accounts');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    loadGuestAccounts();
  }, [loadGuestAccounts]);

  const selectedGuest = pendingGuests.find((g) => g.id === selectedGuestId) || null;
  const totalPending = pendingGuests.length;
  const totalDeclined = declinedGuests.length;

  const handleMergeRequest = async () => {
    if (!selectedGuestId) return;
    try {
      setError(null);
      const preview = await getGuestAccountPreview(selectedGuestId);
      setMergePreview(preview);
      setShowMergeConfirm(true);
    } catch (err) {
      console.error('Error loading merge preview:', err);
      setError('Failed to load guest account details');
    }
  };

  const handleConfirmMerge = async () => {
    if (!selectedGuestId || !mergePreview) return;
    try {
      setMerging(true);
      setError(null);
      const validation = await validateMergeEligibility(selectedGuestId, currentUser.id);
      if (!validation.eligible) {
        throw new Error(validation.reason);
      }
      const clubIds = mergePreview.clubs.map((club) => club.id).filter(Boolean);
      const result = await mergeGuestAccountAndJoinClubs(
        selectedGuestId,
        currentUser.id,
        clubIds
      );
      const mergedOk =
        result?.status === 'merged' ||
        result?.club_joined === true ||
        result?.club_joined === 'true';

      if (mergedOk) {
        const previewClubIds = (mergePreview.clubs || [])
          .map((club) => club.id)
          .filter(Boolean);
        const redirectClubId =
          result.clubs_joined?.find(Boolean) ??
          previewClubIds[0] ??
          null;

        setShowMergeConfirm(false);
        setMergePreview(null);
        if (!homeDashboardSection) {
          setMergeSuccess({
            name: mergePreview.guest.name,
            games: result.games_moved ?? mergePreview.counts.games,
            clubs:
              result.clubs_joined?.length ??
              result.clubs_affected ??
              mergePreview.counts.clubs,
          });
        }

        if (homeDashboardSection) {
          onGuestsResolved?.();
          const targetPath = redirectClubId ? `/clubs/${redirectClubId}` : '/clubs';
          navigate(targetPath, { replace: false });
          return;
        }

        await loadGuestAccounts();
        onGuestsResolved?.();
      } else if (result.status === 'already_merged') {
        setError('This guest account has already been linked');
        setShowMergeConfirm(false);
      } else {
        throw new Error('Link failed');
      }
    } catch (err) {
      console.error('Error merging guest account:', err);
      setError(err.message || 'Failed to link guest account');
      setShowMergeConfirm(false);
    } finally {
      setMerging(false);
    }
  };

  const handleDeclineRequest = () => {
    if (!selectedGuest) return;
    setShowDeclineModal(true);
  };

  const handleDeclineComplete = async () => {
    setShowDeclineModal(false);
    setMergePreview(null);
    await loadGuestAccounts();
    onGuestsResolved?.();
  };

  const handleReconsider = async (guestId) => {
    try {
      await reconsiderGuestAccountMerge(guestId, currentUser.id);
      await loadGuestAccounts();
      onGuestsResolved?.();
    } catch (err) {
      console.error('Error reconsidering merge:', err);
      setError('Failed to reconsider');
    }
  };

  if (loading) {
    if (profileHubSection || homeDashboardSection) return null;
    return (
      <Card variant="default" size="medium">
        <div className="py-8 text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          <Text className="text-text-secondary">Loading guest accounts...</Text>
        </div>
      </Card>
    );
  }

  if (totalPending === 0 && totalDeclined === 0) {
    if (profileHubSection || homeDashboardSection) return null;
    return (
      <Card variant="default" size="medium">
        <Heading level={3} className="mb-2">
          Guest accounts
        </Heading>
        <Text className="text-text-secondary">
          No guest accounts found to link. If you&apos;ve played as a guest before, make sure your
          email matches the email assigned to your guest account.
        </Text>
      </Card>
    );
  }

  if (homeDashboardSection && totalPending === 0) {
    return null;
  }

  const showDeclinedSection = totalDeclined > 0 && !homeDashboardSection;

  const mergeConfirmMessage = mergePreview
    ? `Link "${mergePreview.guest.name}" to your account? This will transfer ${mergePreview.counts.games} game${mergePreview.counts.games !== 1 ? 's' : ''}, ${mergePreview.counts.elo_records} ELO record${mergePreview.counts.elo_records !== 1 ? 's' : ''}, and join you to ${mergePreview.counts.clubs} club${mergePreview.counts.clubs !== 1 ? 's' : ''}. This cannot be undone.`
    : '';

  const isHome = homeDashboardSection;

  const body = (
    <div className={isHome ? 'space-y-3' : 'space-y-5'}>
      {error ? (
        <Card variant="danger" size="small">
          <Text className="text-sm text-danger">{error}</Text>
        </Card>
      ) : null}

      {mergeSuccess ? (
        <Card variant="success" size="small">
          <Text className="text-sm text-success-dark">
            <span className="font-medium">{mergeSuccess.name}</span> linked to your account (
            {mergeSuccess.games} game{mergeSuccess.games !== 1 ? 's' : ''},{' '}
            {mergeSuccess.clubs} club{mergeSuccess.clubs !== 1 ? 's' : ''}).
          </Text>
        </Card>
      ) : null}

      {totalPending > 0 ? (
        <div className={isHome ? 'space-y-3' : 'space-y-4'}>
          {!isHome && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              {profileHubSection ? (
                <Text className="text-sm font-medium text-text-primary">
                  Pending guest accounts
                </Text>
              ) : (
                <Heading level={3} className="text-base sm:text-lg">
                  Pending guest accounts
                </Heading>
              )}
              <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                {totalPending === 1
                  ? 'We found a guest profile linked to your email.'
                  : `We found ${totalPending} guest profiles linked to your email. Select yours to link.`}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {totalPending > 1 ? (
                <Button variant="outline" size="small" onClick={() => setShowBulkMergeModal(true)}>
                  Link all ({totalPending})
                </Button>
              ) : null}
            </div>
          </div>
          )}

          <div
            className={
              isHome
                ? 'max-h-48 space-y-2 overflow-y-auto'
                : 'max-h-72 space-y-2 overflow-y-auto'
            }
          >
            {pendingGuests.map((guest) => (
              <GuestAccountSummaryCard
                key={guest.id}
                guest={guest}
                compact={isHome}
                selected={selectedGuestId === guest.id}
                selectable={totalPending > 1}
                onSelect={() => setSelectedGuestId(guest.id)}
                clubsLabel={
                  guest.clubs_label
                    ? `Clubs: ${guest.clubs_label}`
                    : null
                }
              />
            ))}
          </div>

          {totalPending > 1 && !selectedGuestId ? (
            <div className="rounded-lg border border-warning/40 bg-warning-light p-2.5">
              <p className="text-xs text-warning-dark sm:text-sm">
                <span className="font-medium">Select a guest account above</span> to continue.
              </p>
            </div>
          ) : null}

          <GuestLinkActionChooser
            variant="accountLink"
            compact={isHome}
            disabled={!selectedGuestId}
            loading={merging}
            onMerge={handleMergeRequest}
            onDecline={handleDeclineRequest}
            secondaryAction={
              isHome && totalPending > 1
                ? {
                    label: `Link all (${totalPending})`,
                    onClick: () => setShowBulkMergeModal(true),
                  }
                : null
            }
          />
        </div>
      ) : null}

      {showDeclinedSection ? (
        <div className="border-t border-border-light pt-4">
          <button
            type="button"
            onClick={() => setShowDeclined(!showDeclined)}
            className="mb-3 flex w-full items-center justify-between gap-2"
          >
            <Text className="text-sm font-medium text-text-primary">
              Declined ({totalDeclined})
            </Text>
            <svg
              className={`h-4 w-4 shrink-0 text-text-muted transition-transform ${showDeclined ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showDeclined ? (
            <div className="space-y-2">
              {declinedGuests.map((guest) => {
                const clubNames = (guest.players_club || [])
                  .map((pc) => pc.clubs?.name)
                  .filter(Boolean)
                  .join(', ');
                return (
                  <div key={guest.id} className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <GuestAccountSummaryCard
                        guest={guest}
                        clubsLabel={clubNames ? `Clubs: ${clubNames}` : null}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="small"
                      className="shrink-0 self-center"
                      onClick={() => handleReconsider(guest.id)}
                    >
                      Reconsider
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {showDeclineModal && selectedGuest ? (
        <GuestAccountDeclineModal
          guestId={selectedGuest.id}
          userId={currentUser.id}
          guestName={`${selectedGuest.first_name} ${selectedGuest.last_name || ''}`.trim()}
          onClose={() => setShowDeclineModal(false)}
          onDeclineComplete={handleDeclineComplete}
        />
      ) : null}

      {showBulkMergeModal ? (
        <GuestAccountBulkMergeModal
          guestIds={pendingGuests.map((g) => g.id)}
          userId={currentUser.id}
          guestAccounts={pendingGuests}
          onClose={() => setShowBulkMergeModal(false)}
          onMergeComplete={async (bulkResult) => {
            let redirectClubId = null;
            if (homeDashboardSection) {
              for (const row of bulkResult?.individual_results || []) {
                if (row.status !== 'merged') continue;
                redirectClubId =
                  row.clubs_joined?.find(Boolean) ??
                  row.merge_club_ids?.find(Boolean) ??
                  null;
                if (redirectClubId) break;
              }
            }

            setShowBulkMergeModal(false);

            if (homeDashboardSection) {
              onGuestsResolved?.();
              navigate(redirectClubId ? `/clubs/${redirectClubId}` : '/clubs', { replace: false });
              return;
            }

            await loadGuestAccounts();
            onGuestsResolved?.();
          }}
        />
      ) : null}

      <ConfirmationModal
        isOpen={showMergeConfirm}
        onClose={() => {
          if (!merging) {
            setShowMergeConfirm(false);
            setMergePreview(null);
          }
        }}
        onConfirm={handleConfirmMerge}
        title="Confirm link"
        message={mergeConfirmMessage}
        confirmText={merging ? 'Linking…' : 'Yes, link account'}
        isDangerous={false}
      />
    </div>
  );

  if (profileHubSection) {
    return (
      <section className="rounded-2xl border border-border bg-surface px-4 pb-4 pt-4 shadow-sm">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-text-muted">
          Guest account linking
        </h3>
        {body}
      </section>
    );
  }

  if (homeDashboardSection) {
    const clubHint =
      totalPending === 1 && pendingGuests[0]?.clubs_label
        ? pendingGuests[0].clubs_label.split(',')[0]?.trim()
        : null;

    return (
      <div className="mt-8 mb-10">
        <div className="mb-1 flex items-center justify-between gap-3">
          <Heading level={2} className={homeSectionHeading}>
            {clubHint ? `Link your account to ${clubHint}` : 'Link your account to a club'}
          </Heading>
          <Button as={Link} to="/profile" variant="text" size="small" className="shrink-0 text-sm font-semibold text-primary">
            Player Hub
          </Button>
        </div>
        <Text className="mb-5 block text-sm text-text-secondary">
          {totalPending === 1
            ? 'Review this link request to bring your guest history and ratings into your account.'
            : `You have ${totalPending} link requests — review each one to join your clubs with your game history.`}
        </Text>
        <section className={`${homeGuestMergeShell} p-4 sm:p-5`}>{body}</section>
      </div>
    );
  }

  return body;
};

export default GuestAccountMergesSection;
